import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
} from "firebase/auth";
import { auth } from "../lib/firebaseClient";
import { setTokenProvider } from "../lib/authToken";
import { isCurrentUserAuthorized } from "../lib/authorizedUsersClient";
import { prefersRedirectAuth } from "../lib/authUtils";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    setTokenProvider(async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        return null;
      }
      return currentUser.getIdToken();
    });

    let unsubscribe;

    const initAuth = async () => {
      try {
        await getRedirectResult(auth);
      } catch (error) {
        if (error.code !== "auth/popup-closed-by-user") {
          setAuthError(error.message || "Failed to complete sign-in.");
        }
      }

      unsubscribe = onAuthStateChanged(auth, async (nextUser) => {
        setUser(nextUser);
        setAuthError(null);

        if (!nextUser) {
          setIsAuthorized(false);
          setLoading(false);
          return;
        }

        try {
          const authorized = await isCurrentUserAuthorized(nextUser.uid);
          setIsAuthorized(authorized);
        } catch (error) {
          setIsAuthorized(false);
          setAuthError(error.message || "Failed to verify account access.");
        } finally {
          setLoading(false);
        }
      });
    };

    initAuth();

    return () => {
      unsubscribe?.();
    };
  }, []);

  const signInWithGoogle = async () => {
    setAuthError(null);
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });

    try {
      if (prefersRedirectAuth()) {
        await signInWithRedirect(auth, provider);
        return;
      }

      await signInWithPopup(auth, provider);
    } catch (error) {
      if (error.code !== "auth/popup-closed-by-user") {
        setAuthError(error.message || "Failed to sign in with Google.");
      }
      throw error;
    }
  };

  const signOut = async () => {
    setAuthError(null);
    setIsAuthorized(false);
    await firebaseSignOut(auth);
  };

  const value = useMemo(
    () => ({
      user,
      loading,
      authError,
      isAuthorized,
      signInWithGoogle,
      signOut,
    }),
    [user, loading, authError, isAuthorized],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
